import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { useDebounce } from 'uikit';

import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimIsUniqueContractSubObjectNameGql } from '../BimContractStructureRequests';

import type { ItemTableContractObject } from './useBimItemsContractStructureTable';

interface IUseBimUniqueContractSubObject {
    name: string;
    contractObject?: ItemTableContractObject;
    parentContractObjectId?: number;
    isDirtyObjectName?: boolean;
}

export const useBimUniqueContractSubObject = (props: IUseBimUniqueContractSubObject) => {
    const dispatch = useDispatch();

    const { contractObject, parentContractObjectId, name, isDirtyObjectName } = props;

    const variables = useMemo(() => {
        if (!contractObject?.id && !name?.length) {
            return undefined;
        }

        return {
            contractObjectId: parentContractObjectId || contractObject?.parentId,
            contractSubObjectId: contractObject?.id,
            name: name?.trim(),
        };
    }, [contractObject?.id, contractObject?.parentId, parentContractObjectId, name]);

    const [
        bimUniqueContractSubObjectData,
        bimUniqueContractSubObjectStatus,
        bimUniqueContractSubObjectError,
        bimUniqueContractSubObjectRequest,
    ] = useRequest({
        requestId: 'bimIsUniqueContractSubObjectName',
        query: bimIsUniqueContractSubObjectNameGql,
        withEffect: false,
    });

    const data = useMemo(
        () => bimUniqueContractSubObjectData?.bimIsUniqueContractSubObjectName,
        [bimUniqueContractSubObjectData?.bimIsUniqueContractSubObjectName],
    );

    const debounceRequest = useDebounce(() => {
        if (name && isDirtyObjectName) {
            dispatch(bimUniqueContractSubObjectRequest(variables));
        }
    }, 300);

    const [resetBimUniqueContractSubObjectName] = useResetStoreData({ requestId: 'bimIsUniqueContractSubObjectName' });

    useEffect(() => {
        if (name?.length) {
            debounceRequest();
        }
    }, [debounceRequest, name?.length]);

    return {
        bimUniqueContractSubObjectData: data,
        bimUniqueContractSubObjectStatus,
        bimUniqueContractSubObjectError,
        bimUniqueContractSubObjectRequest,
        resetBimUniqueContractSubObjectName,
    };
};
